// ============================================================================
// PtLogo — PatientTrac shield mark
// ----------------------------------------------------------------------------
// Pure SVG, no external assets. Renders crisply at any size. Uses the gold
// (#c9a96e) and darker navy (#060e1c) from the HUD design tokens.
//
// Three faces:
//   <PtLogo />                   — default 40px, used in app headers
//   <PtLogo size={80} />         — larger, used on login/setup-mfa
//   <PtLogo monochrome />        — single-color version for footers/print
//
// Composition: rounded shield silhouette, two crossed scalpels inside,
// a subtle circuitry trace behind them, a rivet/dot at the top representing
// the OR light. Reads as "surgical + technical + secure" without any
// hallucinated text or AI-image artifacts.
// ============================================================================

interface PtLogoProps {
    size?: number;
    monochrome?: boolean;
    className?: string;
}

export default function PtLogo({ size = 40, monochrome = false, className = '' }: PtLogoProps) {
    // Two-tone palette uses the gold for the shield outline + scalpels and
    // a slightly muted gold for the circuitry traces (so the mark reads
    // foreground/background rather than busy).
    const gold = '#c9a96e';
    const goldDim = '#8b7548';
    const bg = '#060e1c';

    const stroke = monochrome ? gold : gold;
    const accent = monochrome ? gold : goldDim;
    const fill = monochrome ? 'none' : bg;

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 64 64"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            role="img"
            aria-label="PatientTrac"
        >
            {/* Shield body — fill so the mark sits well on light or dark backgrounds */}
            <path
                d="M32 4
                   L56 12
                   L56 30
                   C56 44 46 54 32 60
                   C18 54 8 44 8 30
                   L8 12 Z"
                fill={fill}
                stroke={stroke}
                strokeWidth="2.5"
                strokeLinejoin="round"
            />

            {/* Circuitry traces, left side — drawn behind the scalpels */}
            <g stroke={accent} strokeWidth="1" fill="none" strokeLinecap="round">
                <path d="M14 24 L20 24 L22 26" />
                <path d="M14 30 L18 30" />
                <path d="M14 36 L22 36 L24 38" />
                <circle cx="14" cy="24" r="1" fill={accent} stroke="none" />
                <circle cx="14" cy="30" r="1" fill={accent} stroke="none" />
                <circle cx="14" cy="36" r="1" fill={accent} stroke="none" />
            </g>

            {/* Circuitry traces, right side */}
            <g stroke={accent} strokeWidth="1" fill="none" strokeLinecap="round">
                <path d="M50 24 L44 24 L42 26" />
                <path d="M50 30 L46 30" />
                <path d="M50 36 L42 36 L40 38" />
                <circle cx="50" cy="24" r="1" fill={accent} stroke="none" />
                <circle cx="50" cy="30" r="1" fill={accent} stroke="none" />
                <circle cx="50" cy="36" r="1" fill={accent} stroke="none" />
            </g>

            {/* Crossed scalpels — handles to the bottom corners, blades pointing up */}
            <g stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
                {/* Left scalpel: handle bottom-left to blade upper-right */}
                <path d="M22 48 L36 22" />
                {/* blade tip */}
                <path d="M36 22 L39 19 L37 25 Z" fill={stroke} />

                {/* Right scalpel: handle bottom-right to blade upper-left */}
                <path d="M42 48 L28 22" />
                {/* blade tip */}
                <path d="M28 22 L25 19 L27 25 Z" fill={stroke} />
            </g>

            {/* OR light dot at top of shield — small, focal */}
            <circle cx="32" cy="14" r="2" fill={stroke} />
            <circle cx="32" cy="14" r="3.5" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.5" />
        </svg>
    );
}
